import {
  Button,
  Flex,
  FormControl,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { HookFormCounter } from '@texas/components/shared/form/HookFormCounter';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  freightCostsApi,
  CreateFreightCostRequest,
} from '@texas/api/endpoints/metadata/freightCostsApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ServerError } from '@texas/types';
import { request } from '@texas/utils/helpers/httpHelpers';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';

export function CreateFreightCost({
  organizationId,
  onCreated,
}: {
  organizationId: number;
  onCreated: () => void;
}) {
  const toast = useToast();
  const { t } = useTranslation();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const {
    request: createRequest,
    error,
    loading,
  } = useApiRequest(freightCostsApi.createFreightCost);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm<CreateFreightCostRequest>({ defaultValues: { name: '' } });
  const onSubmit = async (data: CreateFreightCostRequest) => {
    await request(
      createRequest,
      [organizationId, data],
      () => {
        toast({
          title: t('general.created'),
          status: 'success',
          isClosable: true,
        });
        reset();
        onClose();
        onCreated();
      },
      (error: ServerError) => {
        toast({
          title: t('general.createFailed'),
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );
  };

  return (
    <>
      <Button variant="entityDirectoryPrimary" onClick={onOpen}>
        {t('general.addNew')}
      </Button>
      <Modal
        variant="entityDirectory"
        onClose={() => {
          reset();
          onClose();
        }}
        isOpen={isOpen}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader fontWeight="bold">
            {t('configuration.AddNewFreightCost')}
          </ModalHeader>
          <ModalBody>
            {error && <ErrorDetails error={error} />}
            <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
              <Flex direction="column" gap={4}>
                <FormControl isRequired={true} isInvalid={!!errors.name}>
                  <TexasFormLabel>{t('general.name')}</TexasFormLabel>
                  <Input
                    variant="entityDirectory"
                    {...register('name', {
                      required: true,
                      maxLength: {
                        value: 100,
                        message: t('errors.maxLength', { count: 100 }),
                      },
                    })}
                    placeholder={t('general.name')}
                  />
                  <HookFormCounter
                    control={control}
                    name="name"
                    maxLength={100}
                  />
                  <ErrorLabel text={errors.name?.message} />
                </FormControl>
                <FormControl isRequired={true} isInvalid={!!errors.value}>
                  <TexasFormLabel>{t('general.value')}</TexasFormLabel>
                  <Input
                    variant="entityDirectory"
                    {...register('value', {
                      required: true,
                      valueAsNumber: true,
                    })}
                    placeholder={t('general.value')}
                  />
                  <ErrorLabel text={errors.value?.message} />
                </FormControl>
                <FormControl
                  isRequired={true}
                  isInvalid={!!errors.displayOrder}
                >
                  <TexasFormLabel>{t('branch.displayOrder')}</TexasFormLabel>
                  <Input
                    type="number"
                    variant="entityDirectory"
                    {...register('displayOrder', {
                      required: true,
                      valueAsNumber: true,
                    })}
                    placeholder={t('branch.displayOrder')}
                  />
                  <ErrorLabel text={errors.displayOrder?.message} />
                </FormControl>
                <SubmitButton loading={loading}>
                  {t('general.create')}
                </SubmitButton>
              </Flex>
            </form>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
